// Integrations tab — rebuilt 2026-08-05 around mod-fleet-state.
// Shows ONLY external services (register records + fleet-observed), plus a small
// Aeronet-core section. Sign-off / rescan / refresh removed (pre-fleet-state era).
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  getCustomerIntegrations,
  putCustomerIntegrationRecord,
  getObjectText,
  uploadFile,
  type CustomerIntegrationsView,
  type FleetAliasState,
  type IntegrationRecord,
  type DocKind,
} from "../../lib/brain-api";
import { relDate } from "../../mui/format";
import { brainTokens } from "../../design/brainTheme";
import { SectionCard, StatusPill, Icon, EmptyState } from "../../mui";

const c = brainTokens.color;
const DETAIL_TABS = ["overview", "evidence", "docs", "code", "runbooks", "sops"] as const;
type DetailTab = (typeof DETAIL_TABS)[number];

// Core stack services — Aeronet's own platform, never external integrations.
const CORE_SERVICES = new Set(["cognito", "frontend", "api-v5", "fastapi-serverless"]);

/** Merge external service statuses across a customer's fleet aliases — present wins. */
function fleetServices(aliases: FleetAliasState[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const a of aliases) {
    for (const [name, status] of Object.entries(a.services)) {
      if (CORE_SERVICES.has(name)) continue;
      if (out[name] !== "present") out[name] = status;
    }
  }
  return out;
}

function MonoChip({ label, tone = "neutral" }: { label: string; tone?: "neutral" | "warn" }) {
  return (
    <Box
      component="span"
      sx={{
        fontFamily: brainTokens.font.mono,
        fontSize: 11.5,
        px: "8px",
        py: "3px",
        borderRadius: "6px",
        border: `1px solid ${tone === "warn" ? c.warnBorder : c.border}`,
        background: tone === "warn" ? c.warnBg2 : c.canvas,
        color: tone === "warn" ? c.warnText3 : c.ink3,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
}

/**
 * Aeronet core stack per fleet alias — the stuff we care about (Roxen, FastAPI,
 * React, live/archive). No instance ids, MySQL, OS. Shared by the customer
 * Overview tab and the Integrations tab.
 */
export function AeronetCoreCard({ aliases }: { aliases: FleetAliasState[] }) {
  if (!aliases.length) return null;
  return (
    <SectionCard label="Aeronet core · observed" padding={16} headerMb={10} sx={{ mb: "14px" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {aliases.map((a) => (
          <Box key={a.alias} sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 12.5, fontWeight: 500, color: c.ink2, mr: "2px" }}>
              {a.alias}
            </Box>
            {a.stack.siteStatus && (
              <StatusPill tone={a.stack.siteStatus === "live" ? "success" : "neutral"} label={a.stack.siteStatus} />
            )}
            <StatusPill
              tone={a.stack.profile === "new-stack" ? "success" : a.stack.profile === "mixed" ? "warning" : "neutral"}
              label={a.stack.profile}
            />
            {a.stack.roxen && <MonoChip label={`Roxen ${a.stack.roxen}`} />}
            {a.stack.fastapi && <MonoChip label={`FastAPI ${a.stack.fastapi}`} />}
            {!a.stack.fastapi && a.stack.fastapiCommit && (
              <MonoChip label={`FastAPI · ${a.stack.fastapiCommit.slice(0, 7)}`} tone="warn" />
            )}
            {a.stack.react && <MonoChip label={`React ${a.stack.react}`} />}
            {a.stack.reactUnstamped && (
              <MonoChip
                label={`React unstamped${a.stack.reactDeployedAt ? ` · ${a.stack.reactDeployedAt.slice(0, 10)}` : ""}`}
                tone="warn"
              />
            )}
            <Box sx={{ flex: 1 }} />
            {a.observedAt && (
              <Box sx={{ fontSize: 11, color: c.faint, fontFamily: brainTokens.font.mono }}>
                observed {relDate(Date.parse(a.observedAt))}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </SectionCard>
  );
}

/** Self-loading wrapper so the customer Overview tab can drop the card in with just an id. */
export function AeronetCoreCardForCustomer({ customerId }: { customerId: string }) {
  const [aliases, setAliases] = useState<FleetAliasState[] | null>(null);
  useEffect(() => {
    let on = true;
    getCustomerIntegrations(customerId)
      .then((v) => on && setAliases(v.fleet?.available ? v.fleet.aliases : []))
      .catch(() => on && setAliases([]));
    return () => {
      on = false;
    };
  }, [customerId]);
  if (!aliases?.length) return null;
  return <AeronetCoreCard aliases={aliases} />;
}

function statusTone(s: string): "warning" | "success" | "neutral" | "danger" {
  if (s === "draft") return "warning";
  if (s === "confirmed" || s === "deployed") return "success";
  if (s === "retired" || s === "not-deployed") return "danger";
  return "neutral";
}

export function CustomerIntegrationsTabMui({ customerId, isAdmin }: { customerId: string; isAdmin: boolean }) {
  const { enqueueSnackbar } = useSnackbar();
  const [params, setParams] = useSearchParams();
  const selectedKey = params.get("integration");
  const [view, setView] = useState<CustomerIntegrationsView | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setView(await getCustomerIntegrations(customerId));
    } catch {
      enqueueSnackbar("Failed to load integrations.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [customerId, enqueueSnackbar]);

  useEffect(() => {
    void load();
  }, [load]);

  const records = view?.records ?? [];
  const selected = useMemo(
    () => records.find((r) => r.integrationKey === selectedKey) ?? null,
    [records, selectedKey],
  );

  const openDetail = (key: string | null) => {
    const next = new URLSearchParams(params);
    if (key) next.set("integration", key);
    else next.delete("integration");
    setParams(next, { replace: true });
  };

  if (loading) return <Box sx={{ color: c.muted2, fontSize: 14, py: 4 }}>Loading integrations…</Box>;
  if (!view) return null;

  if (selected) {
    return (
      <IntegrationDetail
        customerId={customerId}
        view={view}
        record={selected}
        isAdmin={isAdmin}
        onBack={() => {
          openDetail(null);
          void load();
        }}
        onChanged={async () => {
          await load();
        }}
      />
    );
  }

  const fleet = view.fleet;
  const observed = fleet?.available ? fleetServices(fleet.aliases) : {};
  const externalRecords = records.filter((r) => !CORE_SERVICES.has(r.integrationKey));
  const observedOnly = Object.entries(observed).filter(
    ([name]) =>
      !externalRecords.some((r) => r.integrationKey === name) &&
      !view.features.some((f) => f.featureKey === name),
  );
  const docOnlyFeatures = view.features.filter(
    (f) => !CORE_SERVICES.has(f.featureKey) && !externalRecords.some((r) => r.integrationKey === f.featureKey),
  );
  const nothing = externalRecords.length === 0 && observedOnly.length === 0 && docOnlyFeatures.length === 0;

  return (
    <Box>
      <Box sx={{ fontSize: 12.5, color: c.muted2, mb: "14px" }}>
        External services observed by the mod-fleet-state sweep, plus authored integration records.
        {fleet?.available === false ? " Fleet-state unavailable — showing register records only." : ""}
        {fleet?.available && fleet.aliases.length === 0
          ? " No sweep has seen this customer's aliases yet."
          : ""}
      </Box>

      {nothing ? (
        <EmptyState icon="plug" title="No external integrations">
          Nothing observed on AWS for this customer, and no integration records authored.
        </EmptyState>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", mb: "18px" }}>
          {externalRecords.map((r) => {
            const feature = view.features.find((f) => f.featureKey === r.integrationKey);
            return (
              <Box
                key={r.integrationKey}
                onClick={() => openDetail(r.integrationKey)}
                sx={{
                  background: "#fff",
                  border: `1px solid ${c.border}`,
                  borderRadius: "8px",
                  p: "14px 16px",
                  cursor: "pointer",
                  "&:hover": { borderColor: c.teal },
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start" }}>
                  <Box>
                    <Box sx={{ fontSize: 15, fontWeight: 600, mb: "6px" }}>{r.name}</Box>
                    <Box sx={{ fontSize: 12, color: c.muted2, fontFamily: brainTokens.font.mono }}>{r.integrationKey}</Box>
                  </Box>
                  <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <StatusPill
                      tone={
                        observed[r.integrationKey] === "present"
                          ? "success"
                          : observed[r.integrationKey] === "partial"
                            ? "warning"
                            : "neutral"
                      }
                      label={
                        observed[r.integrationKey]
                          ? `observed · ${observed[r.integrationKey]}`
                          : "not observed"
                      }
                    />
                    <StatusPill tone={statusTone(r.deployed)} label={r.deployed} />
                  </Box>
                </Box>
                {r.summary && (
                  <Box sx={{ mt: "10px", fontSize: 13, color: c.muted, lineHeight: 1.5 }}>{r.summary}</Box>
                )}
                {feature && (
                  <Box sx={{ mt: "10px", fontSize: 12, color: c.muted2 }}>
                    {feature.documents.length} docs
                  </Box>
                )}
              </Box>
            );
          })}

          {observedOnly.map(([name, status]) => (
            <Box
              key={`fleet-${name}`}
              sx={{ background: "#fff", border: `1px dashed ${c.border}`, borderRadius: "8px", p: "14px 16px" }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                  <Box sx={{ fontSize: 15, fontWeight: 600 }}>{name}</Box>
                  <StatusPill tone={status === "present" ? "success" : "warning"} label={`observed · ${status}`} />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <Box sx={{ fontSize: 12.5, color: c.muted2 }}>Observed by fleet-state — no record yet</Box>
                  {isAdmin && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        void putCustomerIntegrationRecord(customerId, name, {
                          name,
                          status: "confirmed",
                          summary: `Observed on AWS via fleet-state (${status}).`,
                        }).then(() => load())
                      }
                    >
                      Add record
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          ))}

          {docOnlyFeatures.map((f) => (
            <Box
              key={f.featureKey}
              sx={{ background: "#fff", border: `1px dashed ${c.border}`, borderRadius: "8px", p: "14px 16px" }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Box sx={{ fontSize: 15, fontWeight: 600 }}>{f.featureKey}</Box>
                  <Box sx={{ fontSize: 12.5, color: c.muted2 }}>Docs only — no record</Box>
                </Box>
                {isAdmin && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      void putCustomerIntegrationRecord(customerId, f.featureKey, {
                        name: f.featureKey,
                        status: "confirmed",
                        summary: f.summary,
                      }).then(() => load())
                    }
                  >
                    Create record
                  </Button>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {fleet?.available && fleet.aliases.length > 0 && <AeronetCoreCard aliases={fleet.aliases} />}
    </Box>
  );
}

function IntegrationDetail({
  customerId,
  view,
  record,
  isAdmin,
  onBack,
  onChanged,
}: {
  customerId: string;
  view: CustomerIntegrationsView;
  record: IntegrationRecord;
  isAdmin: boolean;
  onBack: () => void;
  onChanged: () => Promise<void>;
}) {
  const [tab, setTab] = useState<DetailTab>("overview");
  const [editOpen, setEditOpen] = useState(false);
  const feature = view.features.find((f) => f.featureKey === record.integrationKey);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Button size="small" onClick={onBack} startIcon={<Icon name="arrow-left" size={14} stroke={1.8} />}>
          Back
        </Button>
        <Box sx={{ fontSize: 17, fontWeight: 600, flex: 1 }}>{record.name}</Box>
        <StatusPill tone={statusTone(record.deployed)} label={record.deployed} />
      </Box>

      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Tabs
          orientation="vertical"
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ minWidth: 140, borderRight: 1, borderColor: "divider" }}
        >
          {DETAIL_TABS.map((t) => (
            <Tab key={t} value={t} label={t === "docs" ? "Documentation" : t[0].toUpperCase() + t.slice(1)} sx={{ alignItems: "flex-start", textTransform: "none" }} />
          ))}
        </Tabs>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {tab === "overview" && (
            <SectionCard title="Overview">
              <KV label="Summary" value={record.summary} />
              <KV label="Purpose" value={record.purpose} />
              <KV label="Built by" value={record.builtBy} />
              <KV label="Built at" value={record.builtAt} />
              <KV label="Endpoints" value={record.endpoints?.join(", ")} />
              <KV label="Dependencies" value={record.dependencies?.join(", ")} />
              {isAdmin && (
                <Button sx={{ mt: 2 }} variant="outlined" onClick={() => setEditOpen(true)}>Edit</Button>
              )}
            </SectionCard>
          )}
          {tab === "evidence" && (
            <EvidencePanel
              customerId={customerId}
              record={record}
              isAdmin={isAdmin}
              onChanged={onChanged}
            />
          )}
          {(tab === "docs" || tab === "code" || tab === "runbooks" || tab === "sops") && (
            <DocsPanel
              customerId={customerId}
              integrationKey={record.integrationKey}
              kind={tab === "docs" ? "docs" : tab}
              documents={(feature?.documents ?? []).filter((d) => (d.kind ?? "docs") === (tab === "docs" ? "docs" : tab))}
              isAdmin={isAdmin}
              runbookKey={record.runbookKey}
              onSetRunbook={
                tab === "runbooks" && isAdmin
                  ? async (key) => {
                      await putCustomerIntegrationRecord(customerId, record.integrationKey, { runbookKey: key });
                      await onChanged();
                    }
                  : undefined
              }
              onUploaded={onChanged}
            />
          )}
        </Box>
      </Box>

      <EditDialog
        open={editOpen}
        record={record}
        onClose={() => setEditOpen(false)}
        onSave={async (patch) => {
          await putCustomerIntegrationRecord(customerId, record.integrationKey, patch);
          setEditOpen(false);
          await onChanged();
        }}
      />
    </Box>
  );
}

function KV({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ fontSize: 11, color: c.muted2, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>{label}</Box>
      <Box sx={{ fontSize: 13.5, whiteSpace: "pre-wrap", color: value ? c.ink : c.placeholder }}>{value || "—"}</Box>
    </Box>
  );
}

function EvidencePanel({
  customerId,
  record,
  isAdmin,
  onChanged,
}: {
  customerId: string;
  record: IntegrationRecord;
  isAdmin: boolean;
  onChanged: () => Promise<void>;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [ref, setRef] = useState("");
  const add = async () => {
    if (!ref.trim()) return;
    try {
      await putCustomerIntegrationRecord(customerId, record.integrationKey, {
        evidence: [
          ...record.evidence,
          { source: "manual", ref: ref.trim(), seenAt: new Date().toISOString() },
        ],
      });
      setRef("");
      await onChanged();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Add failed", { variant: "error" });
    }
  };
  return (
    <SectionCard title="Evidence">
      {(record.evidence || []).map((e) => (
        <Box key={`${e.source}:${e.ref}`} sx={{ mb: 1.25, fontSize: 13 }}>
          <StatusPill tone="neutral" label={e.source} />{" "}
          <Box component="a" href={e.ref.startsWith("http") ? e.ref : undefined} sx={{ color: c.tealInk2, wordBreak: "break-all" }}>
            {e.ref}
          </Box>
          {e.note ? <Box sx={{ color: c.muted2, fontSize: 12 }}>{e.note}</Box> : null}
        </Box>
      ))}
      {isAdmin && (
        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
          <TextField size="small" fullWidth placeholder="Manual evidence ref (URL / ARN / note)" value={ref} onChange={(e) => setRef(e.target.value)} />
          <Button variant="outlined" onClick={() => void add()}>Add</Button>
        </Box>
      )}
    </SectionCard>
  );
}

function DocsPanel({
  customerId,
  integrationKey,
  kind,
  documents,
  isAdmin,
  runbookKey,
  onSetRunbook,
  onUploaded,
}: {
  customerId: string;
  integrationKey: string;
  kind: DocKind;
  documents: { key: string; name: string }[];
  isAdmin: boolean;
  runbookKey: string | null;
  onSetRunbook?: (key: string) => Promise<void>;
  onUploaded: () => Promise<void>;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [active, setActive] = useState<string | null>(documents[0]?.key ?? null);
  const [text, setText] = useState("");

  useEffect(() => {
    setActive(documents[0]?.key ?? null);
  }, [documents]);

  useEffect(() => {
    if (!active) {
      setText("");
      return;
    }
    void getObjectText(active)
      .then(setText)
      .catch(() => setText("(failed to load)"));
  }, [active]);

  const upload = async (file: File) => {
    const key = `customers/${customerId}/integrations/${integrationKey}/${kind}/${file.name}`;
    try {
      await uploadFile(key, file, { dept: "company", source: "integration-doc", tier: "company", customer: customerId });
      enqueueSnackbar("Uploaded.", { variant: "success" });
      await onUploaded();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Upload failed", { variant: "error" });
    }
  };

  return (
    <SectionCard title={kind}>
      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ width: 220, flex: "none" }}>
          {documents.map((d) => (
            <Box
              key={d.key}
              onClick={() => setActive(d.key)}
              sx={{
                fontSize: 12.5,
                p: "6px 8px",
                borderRadius: "6px",
                cursor: "pointer",
                bgcolor: active === d.key ? c.panelBg : "transparent",
                fontFamily: brainTokens.font.mono,
                mb: 0.5,
              }}
            >
              {d.name}
              {runbookKey === d.key ? " ★" : ""}
            </Box>
          ))}
          {isAdmin && (
            <Button component="label" size="small" sx={{ mt: 1 }}>
              Upload
              <input hidden type="file" accept=".md,text/markdown,text/plain" onChange={(e) => e.target.files?.[0] && void upload(e.target.files[0])} />
            </Button>
          )}
          {onSetRunbook && active && (
            <Button size="small" sx={{ mt: 1, display: "block" }} onClick={() => void onSetRunbook(active)}>
              Set as primary runbook
            </Button>
          )}
        </Box>
        <Box
          sx={{
            flex: 1,
            fontSize: 13,
            whiteSpace: "pre-wrap",
            fontFamily: brainTokens.font.mono,
            bgcolor: c.panelBg,
            borderRadius: "8px",
            p: 2,
            minHeight: 240,
          }}
        >
          {text || (documents.length ? "…" : "No documents in this kind yet.")}
        </Box>
      </Box>
    </SectionCard>
  );
}

function EditDialog({
  open,
  record,
  onClose,
  onSave,
}: {
  open: boolean;
  record: IntegrationRecord;
  onClose: () => void;
  onSave: (patch: Record<string, unknown>) => Promise<void>;
}) {
  const [name, setName] = useState(record.name);
  const [summary, setSummary] = useState(record.summary ?? "");
  const [purpose, setPurpose] = useState(record.purpose ?? "");
  const [builtBy, setBuiltBy] = useState(record.builtBy ?? "");
  const [builtAt, setBuiltAt] = useState(record.builtAt ?? "");
  const [endpoints, setEndpoints] = useState((record.endpoints ?? []).join(", "));
  const [dependencies, setDependencies] = useState((record.dependencies ?? []).join(", "));
  const [migrationImpact, setMigrationImpact] = useState(record.migrationImpact ?? "");

  useEffect(() => {
    if (!open) return;
    setName(record.name);
    setSummary(record.summary ?? "");
    setPurpose(record.purpose ?? "");
    setBuiltBy(record.builtBy ?? "");
    setBuiltAt(record.builtAt ?? "");
    setEndpoints((record.endpoints ?? []).join(", "));
    setDependencies((record.dependencies ?? []).join(", "));
    setMigrationImpact(record.migrationImpact ?? "");
  }, [open, record]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit {record.integrationKey}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} size="small" />
        <TextField label="Summary" value={summary} onChange={(e) => setSummary(e.target.value)} size="small" multiline minRows={2} />
        <TextField label="Purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)} size="small" multiline minRows={2} />
        <TextField label="Built by" value={builtBy} onChange={(e) => setBuiltBy(e.target.value)} size="small" />
        <TextField label="Built at" value={builtAt} onChange={(e) => setBuiltAt(e.target.value)} size="small" />
        <TextField label="Endpoints (comma-separated)" value={endpoints} onChange={(e) => setEndpoints(e.target.value)} size="small" />
        <TextField label="Dependencies (comma-separated)" value={dependencies} onChange={(e) => setDependencies(e.target.value)} size="small" />
        <TextField label="Migration impact" value={migrationImpact} onChange={(e) => setMigrationImpact(e.target.value)} size="small" multiline minRows={2} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() =>
            void onSave({
              name,
              summary: summary || null,
              purpose: purpose || null,
              builtBy: builtBy || null,
              builtAt: builtAt || null,
              endpoints: endpoints.split(",").map((s) => s.trim()).filter(Boolean),
              dependencies: dependencies.split(",").map((s) => s.trim()).filter(Boolean),
              migrationImpact: migrationImpact || null,
            })
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
